import { useState } from 'react';
import { motion, type Variants } from 'framer-motion';

/**
 * Hero 首屏背景视频（占位示例，正式上线请替换为科技感打字视频）。
 * 视频加载失败时自动回退到渐变海报背景，不影响整体效果。
 */
const VIDEO_SRC =
  '/images/videos.mp4';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

interface HeroProps {
  onStart: () => void;
}

export default function Hero({ onStart }: HeroProps) {
  const [videoFailed, setVideoFailed] = useState(false);

  return (
    <section
      id="home"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      {/* 全屏视频背景 */}
      {!videoFailed && (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={VIDEO_SRC}
          poster="/images/hero-poster.svg"
          autoPlay
          muted
          loop
          playsInline
          onError={() => setVideoFailed(true)}
        />
      )}

      {/* 深色渐变遮罩 */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/95 via-blue-950/90 to-cyan-950/95" />

      {/* 科技感光晕 */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.14),transparent_62%)]" />

      {/* 主内容 */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={container}
        className="relative z-10 mx-auto max-w-4xl px-4 pb-24 pt-28 text-center sm:px-6"
      >
        <motion.div variants={item}>
          <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">
            Technology Innovation
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="text-gradient mt-4 text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl"
        >
          引领科技创新
        </motion.h1>

        <motion.p
          variants={item}
          className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          专注于人工智能、大数据与云计算领域，为企业提供前沿技术解决方案，驱动数字化转型
        </motion.p>

        <motion.div
          variants={item}
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <button
            type="button"
            onClick={onStart}
            className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3.5 text-base font-semibold text-white shadow-xl shadow-cyan-500/30 transition duration-300 hover:shadow-cyan-400/40 hover:brightness-110 sm:w-auto"
          >
            开始合作
          </button>
          <a
            href="#cases"
            className="w-full rounded-full border border-white/25 px-8 py-3.5 text-base font-semibold text-white transition duration-300 hover:border-cyan-400 hover:text-cyan-300 sm:w-auto"
          >
            查看案例
          </a>
        </motion.div>
      </motion.div>

      {/* 底部滚动指示器 */}
      <motion.a
        href="#products"
        aria-label="向下滚动"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
      >
        <span className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/25 p-1.5">
          <span className="h-2 w-1 animate-scroll-dot rounded-full bg-cyan-400" />
        </span>
      </motion.a>
    </section>
  );
}
